import { FC, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { createSelector } from "reselect";
import { DEFAULT_BOT_MESSAGE_DELAY_SECONDS } from "../../constants";
import { useBotTimeout } from "../../hooks/useBotTimeout";
import { useCanHandleBotStep } from "../../hooks/useCanHandleBotSteps";
import { useIsTaskAccepted } from "../../hooks/useIsTaskAccepted";
import { sessionDataHandler } from "../../sessionDataHandler";
import { changeEngagementPhase, setActiveBotStep, setActiveBotStepStatus } from "../../store/actions/genericActions";
import { AppState, BotStepStatus, EngagementPhase } from "../../store/definitions";
import { BotStepType } from "../../types/bots";
import { CheckLoggedInAgentsBotStep } from "../../types/bots/BotSteps/CheckLoggedInAgentsStep";

interface CheckTRAgentStepProps {
    botStep: CheckLoggedInAgentsBotStep;
}

const checkTRAgentStepSelector = createSelector(
    (state: AppState) => state,
    (state) => ({
        isEscalated: state.bot.isEscalated
    })
);

export const CheckTRAgentStep: FC<CheckTRAgentStepProps> = ({ botStep }) => {
    const dispatch = useDispatch();
    const { isTaskAccepted } = useIsTaskAccepted();
    const { canHandleBotStep } = useCanHandleBotStep(botStep, {
        requiredStepType: BotStepType.CheckLoggedInAgents
    });
    const { isEscalated } = useSelector(checkTRAgentStepSelector);

    const handleTimeout = useBotTimeout({
        waitInSeconds: botStep.waitInSeconds ?? DEFAULT_BOT_MESSAGE_DELAY_SECONDS
    });

    useEffect(() => {
        const handleBotStep = async () => {
            if (!canHandleBotStep) {
                return;
            }

            // Mark step as being processed, and hide message input
            dispatch(setActiveBotStepStatus(BotStepStatus.Processing));

            // adds some delay to the bot step
            await handleTimeout.start();
            // If the task was accepted in the wait, then move to the messaging canvas
            if (isTaskAccepted) {
                dispatch(changeEngagementPhase({ phase: EngagementPhase.MessagingCanvas }));
                return;
            }

            const match = (
                await sessionDataHandler.checkTRAgents(
                    botStep.skills,
                    botStep.activities,
                    botStep.operand,
                    botStep.value
                )
            )?.match;

            if (match === undefined) {
                return;
            }

            const nextStepId = botStep.nextStepOptions?.find((option) => {
                switch (option.operator) {
                    case "match":
                        return match;
                    case "nmatch":
                        return !match;
                }
            })?.nextStepId;

            if (nextStepId) {
                dispatch(setActiveBotStepStatus(BotStepStatus.Unhandled));
                dispatch(setActiveBotStep(nextStepId));
            } else if (isEscalated) {
                dispatch(changeEngagementPhase({ phase: EngagementPhase.MessagingCanvas }));
            }
        };
        handleBotStep();
    }, [dispatch, canHandleBotStep, botStep, isEscalated, isTaskAccepted]);

    return <></>;
};
